/**
 * Owned Media → Inicio: la información general del Lab (pedido del equipo,
 * 08/10/2026), sembrada con la PPT "OML Lead Onboarding". Admins y Leads del
 * apartado editan cada sección con el lápiz; el resto la lee.
 *
 * Las credenciales NO van acá (el backend rechaza lo que parece una clave):
 * están en el Vault.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowDown, ArrowUp, ExternalLink, Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { WinclapLoader } from '@/components/ui/winclap-loader';
import { trpc } from '@/lib/trpc';
import { toastError } from '@/lib/toast-error';
import { formatDateTime } from '@/lib/utils';
import type { OmlHomeItem, OmlHomeSection } from '@/../../shared/oml-home';

const k = 'oml.home';
const emptyItem = (): OmlHomeItem => ({ title: '', description: '', url: '', tag: '' });

function move<T>(xs: T[], i: number, d: -1 | 1): T[] {
  const j = i + d;
  if (j < 0 || j >= xs.length) return xs;
  const out = [...xs];
  [out[i], out[j]] = [out[j], out[i]];
  return out;
}

/** Edición de UNA sección: título, texto y tarjetas. */
function SectionEditor({ section, onCancel, onSave, onDelete, saving }: {
  section: OmlHomeSection;
  onCancel: () => void;
  onSave: (s: OmlHomeSection) => void;
  onDelete?: () => void;
  saving: boolean;
}) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<OmlHomeSection>(section);
  const setItem = (i: number, patch: Partial<OmlHomeItem>) =>
    setDraft((d) => ({ ...d, items: d.items.map((it, j) => (j === i ? { ...it, ...patch } : it)) }));

  return (
    <Dialog open onOpenChange={(o) => { if (!o && !saving) onCancel(); }}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
        <DialogHeader><DialogTitle>{t(`${k}.edit_section`, 'Editar sección')}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Input id="oml-home-title" value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} placeholder={t(`${k}.title_ph`, 'Título de la sección')} />
          <Textarea id="oml-home-intro" value={draft.intro} rows={3} onChange={(e) => setDraft({ ...draft, intro: e.target.value })} placeholder={t(`${k}.intro_ph`, 'Texto de la sección (opcional)')} />
          <div className="space-y-2">
            {draft.items.map((it, i) => (
              <div key={i} className="rounded-lg border p-2 space-y-2">
                <div className="grid gap-2 sm:grid-cols-[1fr_140px]">
                  <Input value={it.title} onChange={(e) => setItem(i, { title: e.target.value })} placeholder={t(`${k}.item_title_ph`, 'Título')} />
                  <Input value={it.tag} onChange={(e) => setItem(i, { tag: e.target.value })} placeholder={t(`${k}.item_tag_ph`, 'Rótulo (Deck, Slack…)')} />
                </div>
                <Textarea value={it.description} rows={2} onChange={(e) => setItem(i, { description: e.target.value })} placeholder={t(`${k}.item_desc_ph`, 'Descripción')} />
                <div className="flex items-center gap-2">
                  <Input value={it.url} onChange={(e) => setItem(i, { url: e.target.value })} placeholder="https://…" className="flex-1" />
                  <Button size="icon" variant="ghost" aria-label={t(`${k}.up`, 'Subir')} disabled={i === 0} onClick={() => setDraft((d) => ({ ...d, items: move(d.items, i, -1) }))}><ArrowUp className="h-4 w-4" /></Button>
                  <Button size="icon" variant="ghost" aria-label={t(`${k}.down`, 'Bajar')} disabled={i === draft.items.length - 1} onClick={() => setDraft((d) => ({ ...d, items: move(d.items, i, 1) }))}><ArrowDown className="h-4 w-4" /></Button>
                  <Button size="icon" variant="ghost" aria-label={t(`${k}.remove_item`, 'Quitar tarjeta')} onClick={() => setDraft((d) => ({ ...d, items: d.items.filter((_, j) => j !== i) }))}><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>
            ))}
            <Button size="sm" variant="outline" onClick={() => setDraft((d) => ({ ...d, items: [...d.items, emptyItem()] }))}>
              <Plus className="h-4 w-4 mr-1" />{t(`${k}.add_item`, 'Agregar tarjeta')}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">{t(`${k}.no_secrets`, 'Lo ve todo el apartado: nada de contraseñas ni códigos acá. Las credenciales van al Vault.')}</p>
        </div>
        <DialogFooter className="gap-2 sm:justify-between">
          {onDelete ? <Button variant="ghost" className="text-destructive" disabled={saving} onClick={() => { if (window.confirm(t(`${k}.delete_confirm`, '¿Borrar la sección "{{title}}"? Se puede volver a armar, pero el contenido se pierde.', { title: section.title }))) onDelete(); }}><Trash2 className="h-4 w-4 mr-1" />{t(`${k}.delete_section`, 'Borrar sección')}</Button> : <span />}
          <div className="flex gap-2">
            <Button variant="outline" disabled={saving} onClick={onCancel}>{t('common.cancel', 'Cancelar')}</Button>
            <Button disabled={saving || !draft.title.trim()} onClick={() => onSave(draft)}>{saving ? <WinclapLoader size={14} /> : t('common.save', 'Guardar')}</Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function OmlHomePage() {
  const { t } = useTranslation();
  const utils = trpc.useUtils();
  const q = trpc.oml.home.get.useQuery(undefined, { staleTime: 60_000 });
  const save = trpc.oml.home.save.useMutation({
    // Se usa lo que devolvió el guardado (con su versión nueva): un refetch podía caer en
    // otro contenedor con la versión vieja y el próximo guardado daba un conflicto falso.
    onSuccess: (r) => { toast.success(t(`${k}.saved`, 'Inicio actualizado')); utils.oml.home.get.setData(undefined, (prev) => (prev ? { ...prev, home: r.home } : prev)); setEditing(null); },
    onError: (e) => {
      toastError(e, t(`${k}.save_failed`, 'No se pudo guardar'));
      // Conflicto: se cierra el editor. Con el borrador viejo abierto y la versión nueva
      // ya cargada, un segundo Guardar pisaría el cambio de la otra persona.
      if (e.data?.code === 'CONFLICT') { utils.oml.home.get.invalidate(); setEditing(null); }
    },
  });
  /** Índice de la sección en edición; `sections.length` = sección nueva. */
  const [editing, setEditing] = useState<number | null>(null);

  if (q.isLoading) return <div className="flex justify-center py-24"><WinclapLoader size={40} /></div>;
  if (q.error || !q.data) {
    return (
      <Alert variant="destructive" className="max-w-xl">
        <AlertTitle>{t(`${k}.load_error`, 'No se pudo cargar el Inicio')}</AlertTitle>
        <AlertDescription>{q.error?.message}</AlertDescription>
      </Alert>
    );
  }

  const { home, canEdit } = q.data;
  const sections = home.sections;
  const persist = (next: OmlHomeSection[]) => save.mutate({ sections: next, prevUpdatedAt: home.updatedAt });
  const blank: OmlHomeSection = { id: `section-${Date.now()}`, title: '', intro: '', items: [] };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-[#FF7F5C]">{t(`${k}.kicker`, 'Owned Media Lab')}</p>
          <h1 className="text-2xl font-bold tracking-tight">{t(`${k}.title`, 'Inicio')}</h1>
          <p className="text-sm text-muted-foreground">{t(`${k}.subtitle`, 'Dónde vive cada cosa, cómo trabajamos y quién es quién en el Lab.')}</p>
        </div>
        <div className="flex items-center gap-3">
          {home.updatedAt && <span className="text-xs text-muted-foreground">{t(`${k}.updated`, 'Actualizado {{at}} por {{by}}', { at: formatDateTime(home.updatedAt), by: home.updatedBy ?? '—' })}</span>}
          {canEdit && <Button size="sm" variant="outline" onClick={() => setEditing(sections.length)}><Plus className="h-4 w-4 mr-1" />{t(`${k}.add_section`, 'Agregar sección')}</Button>}
        </div>
      </div>

      <div className="space-y-5">
        {sections.map((s, i) => (
          <Card key={s.id}>
            <CardHeader className="pb-2 flex flex-row items-start justify-between gap-3 space-y-0">
              <CardTitle className="text-lg">{s.title}</CardTitle>
              {canEdit && (
                <div className="flex shrink-0 gap-1">
                  <Button size="icon" variant="ghost" className="h-8 w-8" aria-label={t(`${k}.up`, 'Subir')} disabled={i === 0 || save.isPending} onClick={() => persist(move(sections, i, -1))}><ArrowUp className="h-4 w-4" /></Button>
                  <Button size="icon" variant="ghost" className="h-8 w-8" aria-label={t(`${k}.down`, 'Bajar')} disabled={i === sections.length - 1 || save.isPending} onClick={() => persist(move(sections, i, 1))}><ArrowDown className="h-4 w-4" /></Button>
                  <Button size="icon" variant="ghost" className="h-8 w-8" aria-label={t(`${k}.edit_section`, 'Editar sección')} onClick={() => setEditing(i)}><Pencil className="h-4 w-4" /></Button>
                </div>
              )}
            </CardHeader>
            <CardContent className="space-y-3">
              {s.intro && <p className="text-sm text-muted-foreground whitespace-pre-line">{s.intro}</p>}
              {s.items.length > 0 && (
                <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                  {s.items.map((it, j) => {
                    const body = (
                      <>
                        {it.tag && <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{it.tag}</span>}
                        <div className="flex items-center gap-1.5 font-medium text-sm">
                          {it.title}
                          {it.url && <ExternalLink className="h-3.5 w-3.5 text-brand-green" />}
                        </div>
                        {it.description && <p className="text-xs text-muted-foreground mt-0.5">{it.description}</p>}
                      </>
                    );
                    return it.url
                      ? <a key={j} href={it.url} target="_blank" rel="noopener noreferrer" className="block rounded-lg border p-3 transition-colors hover:bg-muted/60">{body}</a>
                      : <div key={j} className="rounded-lg border p-3">{body}</div>;
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {editing !== null && (
        <SectionEditor
          section={sections[editing] ?? blank}
          saving={save.isPending}
          onCancel={() => setEditing(null)}
          onSave={(next) => persist(editing < sections.length ? sections.map((x, i) => (i === editing ? next : x)) : [...sections, next])}
          onDelete={editing < sections.length ? () => persist(sections.filter((_, i) => i !== editing)) : undefined}
        />
      )}
    </div>
  );
}
